import { PixieEmpty } from "@/components/Polish";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Bell, CornerDownRight, ThumbsUp, Sparkles, Gift, Fish, Sprout, ShieldAlert, Bug } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";

const POLL_MS = 60000;
const ICON = { airdrop: Gift, suggestion_ok: Sparkles, reply: CornerDownRight, like: ThumbsUp, level_up: Sparkles, chest_ready: Gift, bait_ready: Fish, referral: Gift, referral_self: Gift, ticket_reply: CornerDownRight, supporter_on: Sparkles, garden_ready: Sprout, vote_burst: ShieldAlert, app_error: Bug };

function ago(iso, lang) {
  const s = Math.max(1, (Date.now() - new Date(iso).getTime()) / 1000);
  const rtf = new Intl.RelativeTimeFormat(lang === "pt" ? "pt-PT" : "en", { numeric: "auto" });
  if (s < 60) return rtf.format(-Math.round(s), "second");
  if (s < 3600) return rtf.format(-Math.round(s / 60), "minute");
  if (s < 86400) return rtf.format(-Math.round(s / 3600), "hour");
  return rtf.format(-Math.round(s / 86400), "day");
}

/** Header bell: unread badge (polled), dropdown with the last 30 notifications. */
export default function NotificationBell({ className = "" }) {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const location = useLocation();
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const box = useRef(null);

  // patch 30: keyed on the member id (not the user object, which is replaced when /users/me answers), one call on
  // load instead of four, and no polling while the tab is in the background
  const uid = user?.id;
  const poll = useCallback(() => {
    if (!uid) return;
    api.get("/notifications/unread-count").then(({ data }) => setUnread(data.unread || 0)).catch(() => {});
  }, [uid]);

  useEffect(() => {
    const id = setInterval(() => document.visibilityState === "visible" && poll(), POLL_MS);
    return () => clearInterval(id);
  }, [poll]);

  useEffect(() => {
    setOpen(false);
    poll();
  }, [location.pathname, poll]);

  useEffect(() => {
    if (!open) return;
    const close = (e) => box.current && !box.current.contains(e.target) && setOpen(false);
    const esc = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", esc);
    };
  }, [open]);

  async function toggle() {
    const next = !open;
    setOpen(next);
    if (!next) return;
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items || []);
      if (data.unread) {
        await api.post("/notifications/read-all");
        setUnread(0);
      }
    } catch {
      setItems([]);
    }
  }

  if (!user) return null;

  const text = (n) => {
    const where = n.entry_name ? ` ${t.notif.on} ${n.entry_name}` : "";
    if (n.kind === "reply") return `@${n.actor} ${t.notif.replied}${where}`;
    if (n.kind === "like") return `@${n.actor} ${t.notif.liked}${where}`;
    if (n.kind === "level_up") return t.notif.level_up.replace("{n}", n.level);
    if (n.kind === "chest_ready") return t.notif.chest_ready;
    if (n.kind === "bait_ready") return t.notif.bait_ready;
    if (n.kind === "garden_ready") return t.notif.garden_ready.replace("{n}", n.text || "");
    if (n.kind === "app_error") return t.notif.app_error.replace("{e}", n.text || "");
    if (n.kind === "vote_burst") return t.notif.vote_burst.replace("{e}", n.text || "");
    if (n.kind === "ticket_reply") return `${t.tickets.notif}: ${n.text || ""}`;
    if (n.kind === "supporter_on") return t.sup.notif;
    if (n.kind === "airdrop") {   // patch 42: "i.key.2;p.god.1;s..50|message"
      const [code, msg] = (n.text || "").split("|");
      const PK = { god: "God Pack", ed1_plus: "Promo Pack", ed1: "1st Ed. Pack" };
      const parts = (code || "").split(";").filter(Boolean).map((c) => {
        const [k, key, q] = c.split(".");
        const name = k === "i" ? t.pixo.items?.[key] : k === "f" ? t.pixo.foods?.[key] : k === "p" ? PK[key] : "Holo Shards";
        return k === "s" ? `${q} Holo Shards` : `${Number(q) > 1 ? `${q}× ` : ""}${name || key}`;
      });
      return `${t.notif.airdrop}${parts.length ? ` ${parts.join(", ")}` : ""}${msg ? ` · “${msg}”` : ""}`;
    }
    if (n.kind === "suggestion_ok") {
      const [cat, ...rest] = (n.text || "").split("|");
      return t.notif.suggestion_ok.replace("{k}", t.notif.sugg_kind?.[cat] || "").replace("{n}", rest.join("|"));
    }
    if (n.kind === "referral" || n.kind === "referral_self") return t.notif[n.kind].replace("{u}", n.actor);
    return "";
  };
  const SELF = ["airdrop", "suggestion_ok", "level_up", "chest_ready", "bait_ready", "garden_ready", "referral", "referral_self", "supporter_on"];
  const href = (n) => (n.kind === "vote_burst" ? "/admin/dashboard?tab=moderation" : n.kind === "app_error" ? "/admin/dashboard?tab=backups" : n.kind === "ticket_reply" ? `/tickets?t=${n.comment_id}` : n.kind === "suggestion_ok" && n.entry_id ? `/e/${n.entry_id}` : n.kind === "garden_ready" ? `/u/${user.username}?garden=1` : SELF.includes(n.kind) ? `/u/${user.username}` : n.entry_id ? `/e/${n.entry_id}` : "#");

  return (
    <div className={`relative ${className}`} ref={box}>
      <button
        type="button"
        onClick={toggle}
        aria-label={t.notif.title}
        aria-expanded={open}
        data-testid="notif-bell"
        className="relative inline-flex items-center justify-center w-9 h-full text-amber-300 hover:text-amber-200 hover:bg-amber-500/10 transition-colors"
      >
        <Bell className="w-4 h-4" />
        {unread > 0 && (
          <span
            data-testid="notif-badge"
            className="btn-gold absolute top-0.5 right-0 min-w-[16px] h-4 px-1 rounded-full font-bold leading-4 text-center tabular-nums" style={{ fontSize: 10 }}
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div
          data-testid="notif-panel"
          className="fixed left-3 right-3 top-[4.25rem] sm:absolute sm:left-0 sm:right-auto sm:top-full sm:mt-2 z-50 sm:w-[340px] max-h-[70vh] overflow-y-auto rounded-sm border border-amber-500/25 bg-[#121216] shadow-2xl"
        >
          <div className="px-4 py-3 text-[10px] uppercase tracking-[0.25em] font-bold text-amber-400/90 border-b border-amber-500/10">
            {t.notif.title}
          </div>
          {items === null ? (
            <div className="px-4 py-6 text-xs text-stone-500">…</div>
          ) : items.length === 0 ? (
            <div className="px-4 py-2"><PixieEmpty compact title={t.empty.notif_title} hint={t.empty.notif_hint} /></div>
          ) : (
            <ul>
              {items.map((n) => {
                const Icon = ICON[n.kind] || Bell;
                return (
                  <li key={n.id}>
                    <Link
                      to={href(n)}
                      onClick={() => setOpen(false)}
                      className={`flex gap-3 px-4 py-3 border-b border-amber-500/[0.06] hover:bg-amber-500/5 ${n.read ? "" : "bg-amber-500/[0.04]"}`}
                    >
                      <Icon className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${SELF.includes(n.kind) ? "text-amber-300" : "text-stone-400"}`} />
                      <div className="min-w-0">
                        <div className="text-xs text-stone-200 leading-snug">{text(n)}</div>
                        {n.text && !SELF.includes(n.kind) && (
                          <div className="text-[11px] text-stone-500 truncate mt-0.5">“{n.text}”</div>
                        )}
                        <div className="text-[10px] text-stone-600 mt-1">{ago(n.created_at, lang)}</div>
                      </div>
                      {!n.read && <span className="ml-auto mt-1 w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />}
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
